/// <amd-module name="Wit/QueriesHub/ResultsView/ResultsMoreButton" />
import * as React from "react";
import { IFollowsService } from "Notifications/Common/Contracts/FollowsService";
import { IMailConfigurationService } from "Notifications/Common/Services/MailConfigurationService";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { TfsComponent } from "Tfs/Platform/TfsComponent";
import { IObservableValue, ObservableArray, ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { ILegacyPlatformService } from "VSS/Legacy/Legacy";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, IVssLayoutManager } from "VSS/Platform/Layout";
import { IExpandable } from "VSSUI/Expandable";
import { IIdentity } from "VSSUI/IdentityPicker";
import { IListSelection } from "VSSUI/List";
import { IMenuItem, MoreButton } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { QueryType } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { ITeam } from "Wit/Common/BacklogContentService/BacklogContentDataProviderTypes";
import { DataProviders } from "Wit/Common/Constants/DataProviders";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { IQueryService, QueryDataProviders } from "Wit/Common/QueryService/IQueryService";
import { reportError } from "Wit/Common/QueryUtils/ErrorHandlingUtils";
import { LinkQueryMode } from "Wit/Common/QueryUtils/QueryTypeConstants";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import { TempQueryUtils } from "Wit/Common/Utilities/TempQueryUtils";
import * as WorkItemUtils from "Wit/Common/Utils/WorkItemUtils/CommonWorkItemActions";
import { FetchWorkItemsDialog } from "Wit/Common/Utils/WorkItemUtils/FetchWorkItemsDialog";
import { WiqlOperators } from "Wit/Common/WiqlOperators";
import { BulkEditDialog } from "Wit/Common/WorkItemDialogs/Components/BulkEditDialog";
import { CloneWorkItemDialog } from "Wit/Common/WorkItemDialogs/Components/CloneWorkItemDialog";
import { CopyWorkItemDialog } from "Wit/Common/WorkItemDialogs/Components/CopyWorkItemDialog";
import { DeleteDialog } from "Wit/Common/WorkItemDialogs/Components/DeleteDialog";
import { getTemplateMenuItem } from "Wit/Common/WorkItemDialogs/Components/EditTemplateDialog";
import { restoreSelection } from "Wit/Common/WorkItemDialogs/Components/ProcessWorkItemsDialog";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { convertIIdentityToWorkItemIdentityRef } from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { getBoardsMessagesService, getErrorMessage } from "Wit/Messages/BoardsMessagesService";
import { IEditInfo, IQueryContext, IQueryResultItem } from "Wit/QueriesHub/Common/Interfaces";
import { QueryUtils } from "Wit/QueriesHub/Common/Utils/QueryUtils";
import * as Resources from "Wit/QueriesHub/Resources";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItem, IWorkItemPermissionData } from "Wit/WorkItemModel/OM/WorkItemInterfaces";

export interface IResultsMoreButtonProps {
    className?: string;
    onChangeRecycleBin?: () => void;
    onClick?: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
    onDeleteItems?: (item: INamedFieldDataDictionary[]) => void;
    onModifyItems?: (items: IWorkItem[]) => void;
    isImport?: boolean;
    identities: ObservableArray<IIdentity>;
    readOnly?: boolean;
    selection: IListSelection;
    treeItemProvider: ObservableValue<TreeItemProvider<IQueryResultItem | undefined>>;
    itemsReady?: IObservableValue<boolean>;
    getWorkItems?: (idsToUpdate: number[]) => IWorkItem[] | undefined;
}

interface IContributedItemContext {
    closeWorkItemFormDialogDelegate?: () => void;
    columns?: string[];
    currentProjectGuid?: string;
    currentProjectName?: string;
    hideDelete?: boolean;
    ids?: number[];
    query?: IQueryContext;
    rows?: any[][];
    saveErrorCallback?: (error: string) => void;
    tfsContext?: any;
    workItemIds?: number[];
    workItemProjects?: string[];
    workItemTypeNames?: string[];
    workItemAvailable?: boolean;
    workItemId?: number;
}
/**
 * Renders the ... menu button.
 */
export class ResultsMoreButton extends TfsComponent<IResultsMoreButtonProps> implements IExpandable {
    private menuItems = new ObservableCollection<IMenuItem>();
    private defaultItems = new ObservableArray<IMenuItem>();
    private assignToItems = new ObservableArray<IMenuItem>([]);
    private queryService: IQueryService;
    private treeItemProvider: ObservableValue<TreeItemProvider<IQueryResultItem | undefined>>;
    private moreButton = React.createRef<MoreButton>();
    private contributedItemContext: IContributedItemContext = {};
    private hideDelete: boolean = false;
    private projectId: string = "";
    private projectName: string = "";
    private fields = new ObservableArray<IFieldEntry>([]);
    private selectedItems = new ObservableArray<string>([]);
    private workItemPermissions = new ObservableValue<IWorkItemPermissionData | undefined>(undefined);
    private currentIterationPath: string | undefined;
    private useChangeIterationPerfomanceOptimization: boolean;
    private importBulkEditFixEnabled: boolean;

    constructor(props: IResultsMoreButtonProps, context: IVssComponentContext) {
        super(props, context);
        this.queryService = context.pageContext.getService<IQueryService>("IQueryService");
        this.useChangeIterationPerfomanceOptimization = isFeatureFlagEnabled(
            context.pageContext,
            FeatureFlags.UseChangeIterationPerfomanceOptimization,
            false
        );
        this.importBulkEditFixEnabled = isFeatureFlagEnabled(
            this.context.pageContext,
            FeatureFlags.ImportBulkEditFixEnabled,
            false
        );
        this.treeItemProvider = this.props.treeItemProvider;
        this.projectId = this.projectData?.id ?? "";
        this.projectName = this.projectData?.name ?? "";
        this.onIdentitiesChanged();
    }

    public async componentDidMount() {
        this.props.identities.subscribe(this.onIdentitiesChanged);
        this.queryService.queryColumns.subscribe(this.onColumnsChanged);
        this.props.selection.subscribe(this.onSelectionChanged);
        let fields : IFieldEntry[] | undefined;
        if (isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.UseNewWorkItemFieldService, false)) {
            const fieldService = this.context.pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2");
            fields = await fieldService.loadFields();
        } else {
            const fieldService = this.context.pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");
            fields = await fieldService.getFieldsAsync();
        }

        if (fields) {
            this.fields.value = fields;
        }
        if (this.context.pageContext) {
            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            this.workItemPermissions.value = await contributionService.getDataAsync<IWorkItemPermissionData>(
                QueryDataProviders.WorkItemPermissionsDataProvider
            );

            const currentTeam = contributionService.getData<ITeam | undefined>(DataProviders.TeamContextDataProvider);
            const teamSettingsService = this.context.pageContext!.getService<ITeamSettingsService>("ITeamSettingsService");
            teamSettingsService.getTeamSettings(undefined, currentTeam?.id).then(teamSettings => {
                this.currentIterationPath = teamSettings?.currentIteration?.friendlyPath
            });
        }
    }

    public componentDidUpdate() {
        this.defaultItems.value = this.getDefaultMenuItems(this.workItemPermissions.value);
    }

    public componentWillUnmount() {
        this.props.identities.unsubscribe(this.onIdentitiesChanged);
        this.queryService.queryColumns.unsubscribe(this.onColumnsChanged);
        this.props.selection.unsubscribe(this.onSelectionChanged);
    }

    public render() {
        return (
            <Observer permissions={this.workItemPermissions}>
                {(props: { permissions: IWorkItemPermissionData | undefined }) => {
                    return (
                        <MoreButton
                            className={this.props.className}
                            contextualMenuProps={() => ({
                                menuProps: {
                                    id: "queryresultitem-menu",
                                    items: this.menuItems
                                }
                            })}
                            hideDropdownIcon={true}
                            onExpand={() => {
                                if (this.menuItems.length === 0) {
                                    this.getMenuItems(props.permissions);
                                }
                            }}
                            onCollapse={() => {
                                // reset menu items because they can change based on selection
                                this.menuItems = new ObservableCollection();
                                this.defaultItems.value.length = 0;
                            }}
                            ref={this.moreButton}
                            subtle={true}
                            onClick={this.props.onClick}
                        />
                    );
                }}
            </Observer>
        );
    }

    public expand = () => {
        if (this.moreButton.current) {
            this.moreButton.current.expand();
        }
    };

    public collapse = () => {
        if (this.moreButton.current) {
            this.moreButton.current.collapse();
        }
    };

    private setContributedItemContext = async () => {
        this.contributedItemContext.query = await QueryUtils.getQueryContext(this.queryService, this.projectId);
        this.contributedItemContext.currentProjectName = this.projectName;
        this.contributedItemContext.currentProjectGuid = this.projectId;
        this.contributedItemContext.workItemAvailable = true;
        this.contributedItemContext.saveErrorCallback = (error: string) => {
            const messagesService = getBoardsMessagesService(this.context.pageContext);
            messagesService.showMessage(getErrorMessage(error));
        };

        const legacyPlatformService = this.context.pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");
        const modules = await legacyPlatformService.requireModules(["Presentation/Scripts/TFS/TFS.Host.TfsContext"]);
        const tfsContext = modules[0];
        this.contributedItemContext.tfsContext = tfsContext.TfsContext.getDefault();
        this.onColumnsChanged();
        this.onSelectionChanged();
    };

    private onSelectionChanged = () => {
        const selectedItems = this.getSelectedItems();
        this.contributedItemContext.workItemIds = selectedItems.map(item => item[CoreFieldRefNames.Id]);
        this.contributedItemContext.ids = this.contributedItemContext.workItemIds;
        this.selectedItems.value = selectedItems.map(item => item[CoreFieldRefNames.Id]);
        const workItemTypes = new Set<string>();
        let hasNonTestItem = false;
        let hasTestItem = false;
        selectedItems.forEach(item => {
            workItemTypes.add(item[CoreFieldRefNames.WorkItemType]);
            if (WorkItemUtils.TestItemTypes.includes(item[CoreFieldRefNames.WorkItemType])) {
                hasTestItem = true;
            } else {
                hasNonTestItem = true;
            }
        });
        this.contributedItemContext.workItemTypeNames = [...workItemTypes.values()];
        this.contributedItemContext.workItemProjects = selectedItems.map(item => item[CoreFieldRefNames.TeamProject] || this.projectId);
        this.hideDelete = this.props.isImport || selectedItems.some(item => item[CoreFieldRefNames.Id] < 0) || hasTestItem; // hide delete if a test item is part of selection
        this.contributedItemContext.hideDelete = hasNonTestItem;
        this.contributedItemContext.rows = selectedItems.map(item => this.queryService.queryColumns.value.map(column => item[column]));

        // only necessary for test case work item deletion, where you can only delete 1 item at a time
        const selectedItem = selectedItems[0];
        if (selectedItem && this.props.onDeleteItems) {
            this.contributedItemContext.workItemId = selectedItem[CoreFieldRefNames.Id];
            this.contributedItemContext.closeWorkItemFormDialogDelegate = () => {
                this.props.onDeleteItems?.([selectedItem]);
            };
        }
    };

    private getOrFetchSelectedItems = async (): Promise<INamedFieldDataDictionary[]> => {
        const notFetchedWorkItemIds: number[] = [];

        for (const range of this.props.selection.value) {
            for (let i = range.beginIndex; i <= range.endIndex; i++) {
                const item = this.treeItemProvider.value.value[i];
                const queryResultItem = item?.underlyingItem.data;
                if (queryResultItem && !this.queryService.isWorkItemAlreadyFetched(queryResultItem)) {
                    const workItemId = queryResultItem.value[CoreFieldRefNames.Id];
                    notFetchedWorkItemIds.push(workItemId);
                }
            }
        }

        let selectedItems: INamedFieldDataDictionary[] = [];
        if (notFetchedWorkItemIds.length > 0) {
            const selectionCopy = this.props.selection.value.map(item => ({ ...item }));

            selectedItems = await new Promise(resolve => {
                this.openDialog(onDismiss => (
                    <FetchWorkItemsDialog
                        onDismiss={() => {
                            onDismiss();
                            resolve(this.getSelectedItems());
                        }}
                        workItemIds={notFetchedWorkItemIds}
                        processWorkItems={async (workItemIds) => {
                            await this.queryService.fetchWorkItems(workItemIds, { skipUsingOfBatches: true });
                            return true;
                        }}
                        callBack={() => {
                            if (this.useChangeIterationPerfomanceOptimization) {
                                restoreSelection(selectionCopy, this.props.selection);
                            } else {
                                for (const range of selectionCopy) {
                                    this.props.selection.select(range.beginIndex, range.endIndex - range.beginIndex + 1);
                                }
                            }
                        }} />
                ));
            });
        } else {
            selectedItems = this.getSelectedItems();
        }

        return selectedItems;
    };

    private getSelectedItems = (): INamedFieldDataDictionary[] => {
        return QueryUtils.getSelectedItemsFromTreeItemProvider(this.props.selection, this.treeItemProvider.value).map(v => v.value);
    };

    private isCrossProjectItem(item: INamedFieldDataDictionary) {
        return item[CoreFieldRefNames.TeamProject] !== this.projectName;
    }

    private onColumnsChanged = () => {
        this.contributedItemContext.columns = this.queryService.queryColumns.value;
    };

    private openDialog = (callout: (onDismiss: () => void) => JSX.Element): void => {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => callout(onDismiss));
    };

    private onIdentitiesChanged = () => {
        this.assignToItems.splice(
            0,
            this.assignToItems.length,
            ...this.props.identities.value.map<IMenuItem>((identity: IIdentity) => ({
                id: identity.localId!,
                text: identity.displayName,
                iconProps: {
                    render: () => {
                        return <Persona size={PersonaSize.size24} identity={identity} />;
                    }
                },
                onActivate: () => {
                    WorkItemUtils.onAssignToItemActivated(
                        this.context.pageContext,
                        this.getSelectedItems,
                        identity ? convertIIdentityToWorkItemIdentityRef(this.context.pageContext, identity) : undefined,
                        this.props.onModifyItems,
                        this.props.getWorkItems
                    );
                }
            }))
        );
    };

    private generateTempQueryIdForSelection = async (): Promise<string> => {
        const queryService = this.context.pageContext.getService<IQueryService>("IQueryService");
        if (queryService.editorFields.length === 0) {
            await queryService.initializeFields();
        }
        const editinfo: IEditInfo = {
            mode: LinkQueryMode.WorkItems,
            treeLinkTypes: "",
            linkTypes: "",
            sourceFilter: {
                clauses: [
                    {
                        logicalOperator: "",
                        fieldName:
                            queryService.editorFields.value.find(editorField => editorField.referenceName === CoreFieldRefNames.Id)?.name || "",
                        operator: WiqlOperators.OperatorIn,
                        value: this.getSelectedItems()
                            .map(selectedItem => selectedItem.id)
                            .toString(),
                        index: 0,
                        originalIndex: 0
                    }
                ],
                groups: []
            },
            treeTargetFilter: { clauses: [], groups: [] },
            linkTargetFilter: { clauses: [], groups: [] },
            teamProject: ""
        };
        const wiql = await queryService.generateWiql(this.projectId, editinfo);
        let tempQueryId = "";
        try {
            tempQueryId = await TempQueryUtils.beginCreateTemporaryQueryId(this.context.pageContext, wiql, QueryType.Flat);
        } catch (error) {
            reportError(this.context.pageContext, "email-work-items-dialog", error.message);
        }

        return tempQueryId;
    };

    private getMenuItems = async (permissions: IWorkItemPermissionData | undefined) => {
        if (!this.queryService.isRecycleBin) {
            this.defaultItems.value = this.getDefaultMenuItems(permissions);
            this.menuItems.push(this.defaultItems);
            await this.setContributedItemContext();
            this.menuItems.push(
                getMenuItems(this.context.pageContext, "ms.vss-work-web.query-result-work-item-menu", this.contributedItemContext, undefined, true)
            );
            this.menuItems.push(
                getMenuItems(this.context.pageContext, "ms.vss-work-web.work-item-context-menu", this.contributedItemContext, undefined, true)
            );
            this.menuItems.push(
                getMenuItems(this.context.pageContext, "ms.vss-work-web.work-item-context-menu-owp", this.contributedItemContext, undefined, true)
            );
        } else {
            let commandBarItems = this._recycleBinMenuItems;

            const canDeleteWorkItems = permissions?.workItemDeletePermission?.hasPermission;

            const canDestroyWorkItems = permissions?.workItemDestroyPermission?.hasPermission;

            if (!canDeleteWorkItems) {
                commandBarItems = commandBarItems.filter(item => item.id !== "restore-item");
            }

            if (!canDestroyWorkItems) {
                commandBarItems = commandBarItems.filter(item => item.id !== "permanently-delete-item");
            }
            this.menuItems.push(commandBarItems);
        }
    };

    private getDefaultMenuItems = (permissions: IWorkItemPermissionData | undefined): IMenuItem[] => {
        const items: IMenuItem[] = [
            {
                id: "clone-work-item",
                rank: 20,
                disabled: this.getSelectedItems().length !== 1,
                text: Resources.CloneWorkItem,
                groupKey: "create",
                onActivate: () => {
                    this.openDialog(onDismiss => <CloneWorkItemDialog selectedItem={this.getSelectedItems()[0]} onDismiss={onDismiss} />);
                }
            },
            {
                id: "create-copy",
                rank: 30,
                disabled: this.getSelectedItems().length !== 1,
                text: Resources.CreateCopyOfWorkItem,
                groupKey: "create",
                iconProps: { iconName: "Copy" },
                onActivate: () => {
                    this.openDialog(onDismiss => <CopyWorkItemDialog selectedItem={this.getSelectedItems()[0]} onDismiss={onDismiss} />);
                }
            }
        ];

        if (this.queryService.queryResult.value?.data) {
            items.push(
                WorkItemUtils.getCopyAsHTMLCommandMenuItem(
                    this.context.pageContext,
                    "export",
                    this.getOrFetchSelectedItems,
                    this.queryService.queryResult.value.data.columns
                )
            );
        }

        items.push(
            WorkItemUtils.getCopyLinkCommandMenuItem(this.context.pageContext, "export", this.getSelectedItems, this.getSelectedItems().length !== 1)
        );

        if (!this.props.readOnly) {
            const mailConfigurationService = this.context.pageContext.getService<IMailConfigurationService>("IMailConfigurationService");
            const isBulkUnfollowsEnabled = mailConfigurationService.isEnabled();
            items.push(
                {
                    rank: 10,
                    id: "bulk-edit-workitems",
                    text: Resources.BulkEditSelectedWorkItems,
                    iconProps: { iconName: "Edit" },
                    groupKey: "modify",
                    onActivate: () => {
                        this.openDialog(onDismiss => (
                            <Observer fields={this.fields}>
                                {({ fields }: { fields: IFieldEntry[] }) => (
                                    <BulkEditDialog
                                        fields={fields}
                                        onDismiss={onDismiss}
                                        onModifyItems={this.props.onModifyItems}
                                        selectedItems={this.getSelectedItems()}
                                        getWorkItems={this.props.getWorkItems}
                                        allowTempIds={this.importBulkEditFixEnabled && this.props.isImport}
                                    />
                                )}
                            </Observer>
                        ));
                    }
                },
                WorkItemUtils.getChangeTypeCommandMenuItem(
                    this.context.pageContext,
                    "modify",
                    this.getSelectedItems,
                    this.queryService.isCrossProject,
                    this.props.onModifyItems,
                    this.projectId
                ),
                WorkItemUtils.getMoveToTeamProjectCommandMenuItem(
                    this.context.pageContext,
                    "export",
                    this.getSelectedItems,
                    this.props.onModifyItems
                ),
                {
                    rank: 14,
                    id: "assign-work-item",
                    text: Resources.AssignWorkItem,
                    iconProps: { iconName: "People" },
                    groupKey: "modify",
                    subMenuProps: {
                        id: "assign-work-item-submenu",
                        items: this.assignToItems
                    }
                }
            );

            const deletePermission = permissions?.workItemDeletePermission?.hasPermission;

            const hideDeleteButton = !deletePermission || this.hideDelete;
            if (!hideDeleteButton) {
                items.push({
                    rank: 25,
                    id: "delete-work-items",
                    text: Resources.DeleteSelectedWorkItems,
                    iconProps: { iconName: "Cancel", className: "delete-icon" },
                    groupKey: "modify",
                    onActivate: () => {
                        this.openDialog(onDismiss => (
                            <DeleteDialog onDismiss={onDismiss} onDeleteItems={this.props.onDeleteItems} selectedItems={this.getSelectedItems()} />
                        ));
                    }
                });
            }

            if (isBulkUnfollowsEnabled) {
                items.push({
                    rank: 13,
                    id: "unfollow-work-item",
                    text: Resources.BulkUnfollowWorkItems,
                    iconProps: { iconName: "Hide2" },
                    groupKey: "modify",
                    onActivate: () => {
                        const followsService = this.context.pageContext.getService<IFollowsService>("IFollowsService");
                        this.getSelectedItems().forEach(selectedItem => {
                            followsService.unfollow("WorkItem", selectedItem[CoreFieldRefNames.Id].toString()).then(null, reason => {
                                reportError(this.context.pageContext, "QueryResultsMoreButton.unfollow", reason);
                            });
                        });
                        const globalMessagesService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
                        globalMessagesService.addToast({ message: Resources.Unfollowing, duration: 2000 });
                    }
                });
            }
            items.push(WorkItemUtils.getLinkToNewCommandMenuItem(this.context.pageContext, this.getSelectedItems, this.projectId));
            items.push(WorkItemUtils.getLinkToExistingCommandMenuItem(this.context.pageContext, this.getSelectedItems));
            if (this.queryService.queryResult.value?.data) {
                items.push(
                    WorkItemUtils.getEmailCommandMenuItem(
                        this.context.pageContext,
                        this.queryService.queryResult.value.data.columns,
                        this.getSelectedItems,
                        this.generateTempQueryIdForSelection
                    )
                );
            }
            const selectedItems = this.getSelectedItems();
            if (selectedItems.length > 0) {
                const crossProjectItemSelected = !!selectedItems.find(item => this.isCrossProjectItem(item));
                const selectedItemType = selectedItems[0][CoreFieldRefNames.WorkItemType];
                const areSelectedItemsSameType = !selectedItems.find(item => item[CoreFieldRefNames.WorkItemType] !== selectedItemType);
                if (areSelectedItemsSameType && !crossProjectItemSelected) {
                    items.push(
                        getTemplateMenuItem(
                            this.context.pageContext,
                            selectedItems,
                            this.openDialog,
                            this.projectId,
                            {
                                teamData: this.teamData,
                                fields: this.fields,
                                onModifyItems: this.props.onModifyItems,
                                currentIterationPath: this.currentIterationPath
                            }
                        )
                    );
                }
            }

            items.push(WorkItemUtils.getNewBranchCommandMenuItem(this.context.pageContext, this.getSelectedItems));
        }

        return items;
    };

    private readonly _recycleBinMenuItems = [
        WorkItemUtils.getRestoreCommandMenuItem(
            this.context.pageContext,
            this.selectedItems,
            this.props.onChangeRecycleBin,
            /** enabledByDefault */ true
        ),
        WorkItemUtils.getPermanentlyDeleteCommandMenuItem(
            this.context.pageContext,
            this.projectId,
            this.selectedItems,
            this.props.onChangeRecycleBin,
            /** enabledByDefault */ true
        )
    ];
}
